import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { api, unwrap } from "@/client/lib/api";
import { ensureTenantData } from "@/client/lib/session";
import { dayAfter } from "@/features/leave/leave-dates";
import {
  type LeaveBalance,
  type LeaveCount,
  type LeaveCountSearch,
  type LeaveOff,
  type LeaveRequest,
  type LeaveType,
  standing,
} from "@/features/leave/schemas";
import { leaveYearOf } from "@/shared/leave-year";

/** Whom a leave screen names: the route passes the company's people in. */
export interface LeavePerson {
  readonly id: string;
  readonly name: string;
  readonly photoKey: string | null;
}

/** The types and the month the company's leave years start in, read together. */
export const leaveSetupQuery = queryOptions({
  queryKey: ["leave", "types"],
  queryFn: (): Promise<{ startMonth: number; items: LeaveType[] }> =>
    unwrap(api.leave.types.$get()),
});

export const leaveTypesQuery = queryOptions({
  ...leaveSetupQuery,
  select: ({ items }) => items,
});

/** This leave year's balance per paid type, approved leave already taken off. */
export const leaveBalancesQuery = (personId: string) =>
  queryOptions({
    queryKey: ["leave", "balances", personId],
    queryFn: (): Promise<{ year: number; items: LeaveBalance[] }> =>
      unwrap(
        api.people[":personId"].leave.balances.$get({ param: { personId } }),
      ),
  });

/** One person's leave starting in a leave year, by default the current one. */
export const leaveRequestsQuery = (personId: string, year?: number) =>
  queryOptions({
    queryKey: ["leave", "requests", personId, year ?? null],
    queryFn: () =>
      unwrap(
        api.people[":personId"].leave.requests.$get({
          param: { personId },
          query: year === undefined ? {} : { year: String(year) },
        }),
      ).then(({ items }) => items),
  });

/**
 * Leave touching a month, read by the leave year the month is in: an ask is a row per leave year,
 * so every row that touches the month starts in that year.
 */
export const monthLeaveQuery = (
  personId: string,
  month: string,
  startMonth: number,
) => leaveRequestsQuery(personId, leaveYearOf(`${month}-01`, startMonth));

/** A month view's first frame: the types, then the leave of the month's leave year. */
export async function ensureMonthLeave(
  queryClient: QueryClient,
  personId: string,
  month: string,
): Promise<void> {
  const { startMonth } = await ensureTenantData(queryClient, leaveSetupQuery);
  await ensureTenantData(
    queryClient,
    monthLeaveQuery(personId, month, startMonth),
  );
}

/**
 * The server counts the days, since weekends and holidays come out of them. The last count stays
 * on screen while the next is read, so the line under the dates never blinks.
 */
export const leaveCountQuery = (personId: string, search: LeaveCountSearch) =>
  queryOptions({
    queryKey: ["leave", "count", personId, search],
    queryFn: (): Promise<LeaveCount> =>
      unwrap(
        api.people[":personId"].leave.count.$get({
          param: { personId },
          query: {
            startsOn: search.startsOn,
            endsOn: search.endsOn,
            ...(search.leaveTypeId ? { leaveTypeId: search.leaveTypeId } : {}),
            ...(search.half ? { half: search.half } : {}),
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

/** Asks a Manager or an Admin may decide; the route refuses everyone else. */
export const waitingLeaveQuery = queryOptions({
  queryKey: ["leave", "waiting"],
  queryFn: () =>
    unwrap(api.leave.requests.waiting.$get()).then(({ items }) => items),
});

/** Approved leave touching today or the 13 days after. */
export const leaveOffQuery = queryOptions({
  queryKey: ["leave", "off"],
  queryFn: (): Promise<LeaveOff[]> =>
    unwrap(api.leave.off.$get({ query: {} })).then(({ items }) => items),
});

/**
 * The last day of the leave a person is on today, following an ask's rows across a leave year's turn;
 * null when they are not on leave.
 */
export function lastDayAway(
  off: readonly LeaveOff[],
  personId: string,
  today: string,
): string | null {
  const theirs = off.filter((one) => one.personId === personId);
  let through: string | null = null;
  for (
    let covering = theirs.find(
      (one) => one.startsOn <= today && today <= one.endsOn,
    );
    covering;
    covering = theirs.find(
      (one) => one.startsOn === dayAfter(covering?.endsOn ?? ""),
    )
  ) {
    through = covering.endsOn;
  }
  return through;
}

/** One ask is stored as a row per leave year and type; a screen shows it as one range. */
export function asks<Row extends Pick<LeaveRequest, "startsOn" | "endsOn">>(
  rows: readonly Row[],
  key: (row: Row) => string,
): (Row & { readonly startsOn: string; readonly endsOn: string })[] {
  const joined = new Map<string, Row>();
  for (const row of rows) {
    const seen = joined.get(key(row));
    joined.set(
      key(row),
      seen
        ? {
            ...seen,
            startsOn:
              row.startsOn < seen.startsOn ? row.startsOn : seen.startsOn,
            endsOn: row.endsOn > seen.endsOn ? row.endsOn : seen.endsOn,
          }
        : row,
    );
  }
  return [...joined.values()];
}

/**
 * One ask as a row. Its id is the group's, which every row of it shares; `requestId` is one of those
 * rows, which is what a change is sent to.
 */
export interface LeaveAsk extends LeaveRow {
  readonly requestId: string;
  readonly updatedAt: string;
}

/**
 * Withdrawn and cancelled rows draw nothing; a cancel that kept the first days leaves those
 * approved, and they stand for the ask.
 */
export function leaveAsks(rows: readonly LeaveRow[]): LeaveAsk[] {
  const live = rows.filter(
    (row) => row.status !== "withdrawn" && row.status !== "cancelled",
  );
  const halfDays = new Map<string, number>();
  for (const row of live) {
    halfDays.set(row.groupId, (halfDays.get(row.groupId) ?? 0) + row.halfDays);
  }
  return asks(live, (row) => row.groupId).map((ask) => ({
    ...ask,
    id: ask.groupId,
    requestId: ask.id,
    halfDays: halfDays.get(ask.groupId) ?? ask.halfDays,
    updatedAt: "",
  }));
}

/**
 * This leave year and the next, so leave already asked for after the turn shows before it. The
 * leave year is the company's, passed in so no browser clock picks it.
 */
export const personLeaveQuery = (personId: string, year: number) =>
  queryOptions({
    queryKey: ["leave", "requests", personId, "asks", year],
    queryFn: async (): Promise<LeaveAsk[]> => {
      const years = await Promise.all(
        [year, year + 1].map((one) =>
          unwrap(
            api.people[":personId"].leave.requests.$get({
              param: { personId },
              query: { year: String(one) },
            }),
          ),
        ),
      );
      return leaveAsks(years.flatMap(({ items }) => items));
    },
  });
/**
 * Every row of one ask, oldest first; the rows of one ask move together. Who else is off and the
 * balance come in the same answer and are put where the panel reads them.
 */
export const leaveAskQuery = (id: string) =>
  queryOptions({
    queryKey: ["leave", "ask", id],
    queryFn: ({ client }) =>
      unwrap(api.leave.requests[":id"].$get({ param: { id } })).then(
        ({ items, off, balances }) => {
          const rows = standing(items);
          const first = rows[0];
          const last = rows.at(-1);
          if (first && last) {
            client.setQueryData(
              offDuringQuery(first.startsOn, last.endsOn).queryKey,
              off,
            );
          }
          if (first && balances) {
            client.setQueryData(
              leaveBalancesQuery(first.personId).queryKey,
              balances,
            );
          }
          return items;
        },
      ),
  });

export type LeaveAskRows = Awaited<
  ReturnType<NonNullable<ReturnType<typeof leaveAskQuery>["queryFn"]>>
>;
export type LeaveRow = LeaveAskRows[number];

/** Who else is off over an ask's dates: a decider's "Also off then". */
export const offDuringQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: ["leave", "off", from, to, true],
    queryFn: () =>
      unwrap(api.leave.off.$get({ query: { from, to } })).then(
        ({ items }) => items,
      ),
  });

/**
 * A decision changes the ask, the person's list and balance, what waits on a decider and who is
 * off, so every leave list is read again from the server rather than patched here.
 */
export function afterLeaveChange(
  queryClient: QueryClient,
  { items: rows }: { readonly items: LeaveAskRows },
): Promise<unknown> {
  const first = rows[0];
  if (first)
    queryClient.setQueryData(leaveAskQuery(first.groupId).queryKey, rows);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["leave", "requests"] }),
    queryClient.invalidateQueries({ queryKey: ["leave", "balances"] }),
    queryClient.invalidateQueries({ queryKey: ["leave", "off"] }),
    queryClient.invalidateQueries({ queryKey: ["leave", "waiting"] }),
    queryClient.invalidateQueries({ queryKey: ["inbox"] }),
  ]);
}
